/**
 * Product Media Slider Component
 * Handles swipeable product images on collection pages
 * Uses Swiper for smooth sliding interactions
 */

import { Component } from "./component-base.js";

export class ProductMediaSlider extends Component {
  #onParentSliderInit = () => {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.#initSwiper();
  };

  connectedCallback() {
    // Wait for Swiper to be available
    if (typeof window.Swiper === 'undefined') {
      setTimeout(() => this.connectedCallback(), 100);
      return;
    }

    this.closest('[data-slider-block]')?.addEventListener(
      'slider-block:initialized',
      this.#onParentSliderInit
    );

    this.#setupSwatchListener();
    this.#scheduleInit();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.closest('[data-slider-block]')?.removeEventListener(
      'slider-block:initialized',
      this.#onParentSliderInit
    );

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * Defer init until a parent block slider has transformed the DOM.
   * Product lists wrap cards in `.slider-block-swiper` after connect.
   */
  #scheduleInit(attempts = 0) {
    const parentList = this.closest('[data-slider-block]');

    if (parentList && !parentList.querySelector('.slider-block-swiper')) {
      if (attempts < 60) {
        requestAnimationFrame(() => this.#scheduleInit(attempts + 1));
        return;
      }
    }

    this.#initSwiper();
  }

  #isInsideCarousel() {
    return Boolean(this.closest('.slider-block-swiper .swiper-slide'));
  }

  #setupSwatchListener() {
    const swatchComponent = this.closest('[data-product-card]')?.querySelector('swatches-item-component');

    if (swatchComponent) {
      swatchComponent.addEventListener('swatch:change', (e) => {
        this.#onSwatchChange(e.detail.mediaId);
      });
    }
  }

  #onSwatchChange(mediaId) {
    if (!this.swiper || !mediaId) {
      console.warn('[Slider] Missing swiper or mediaId');
      return;
    }

    const slides = this.querySelectorAll('.swiper-slide[data-media-id]');
    const slideIndex = Array.from(slides).findIndex(
      (slide) => slide.dataset.mediaId === mediaId
    );

    if (slideIndex !== -1) {
      this.swiper.slideTo(slideIndex);
    } else {
      console.warn('[Slider] No matching slide found for mediaId:', mediaId);
    }
  }

  /**
   * Initialize Swiper slider
   */
  #initSwiper() {
    const wrapper = this.querySelector('.swiper');

    if (!wrapper) {
      console.warn('[ProductMediaSlider] No .swiper wrapper found');
      return;
    }

    const slides = wrapper.querySelectorAll('.swiper-slide');

    if (slides.length <= 1) {
      return;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    const inCarousel = this.#isInsideCarousel();

    try {
      this.swiper = new window.Swiper(wrapper, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        roundLengths: true,
        navigation: {
          nextEl: wrapper.querySelector('.product-card-controls-left'),
          prevEl: wrapper.querySelector('.product-card-controls-right'),
        },
        // Let the parent product-list carousel handle drag/scroll gestures
        allowTouchMove: !inCarousel,
        simulateTouch: !inCarousel,
        touchStartPreventDefault: false,
        preventClicks: false,
        preventClicksPropagation: false,
      });
    } catch (error) {
      console.error('[ProductMediaSlider] Failed to initialize:', error);
    }
  }
}

customElements.define("product-media-slider", ProductMediaSlider);
